import React, { useCallback, useEffect, useRef, useState } from "react";

import * as API from "@ekonsilio/api-livechat";
import { Transition } from "@headlessui/react";
import { useCookies } from "react-cookie";
import { FormattedMessage } from "react-intl";
import { useQuery } from "react-query";

import MessageGroup from "./messages/MessageGroup";

import useAsyncEffect from "../../utils/hooks/useAsyncEffect";
import useVisualViewport from "../../utils/hooks/useVisualViewport";
import { getBehaviorsSessionStorage } from "../../utils/session-storage";
import { useWebmessenger } from "../../utils/webmessenger";
import { useWebSocket } from "../../utils/websocket";

const webmessengerApi = new API.Messaging.Webmessenger();

function MessageList() {
    const {
        conversation,
        operator,
        greeting,
        typing,
        setTyping,
        setConversation,
        online,
        messages,
        handleSetMessages,
    } = useWebmessenger();

    const container = useRef();
    const markMessagesAsReadSent = useRef({});
    const [, , removeCookie] = useCookies(["eK-conversation"]);

    const { websocket, connected } = useWebSocket();
    const { visualViewport, windowSize } = useVisualViewport();

    const [showTyping, setShowTyping] = useState(false);
    const [conversationTypings, setConversationTypings] = useState({});

    const isClosedByVisitor = conversation?.events.find(
        (event) => event.type === "closedByVisitor"
    );

    const markMessagesAsRead = useCallback(() => {
        const lastUnreadMessage = messages.findLast(
            (message) =>
                !message.status?.readAt && message.author?.type === "operator"
        );

        /** Send read status */
        if (
            conversation?._id &&
            lastUnreadMessage &&
            lastUnreadMessage?._id !== "behavior-greeting-message" &&
            !markMessagesAsReadSent.current[lastUnreadMessage._id]
        ) {
            markMessagesAsReadSent.current = {
                ...markMessagesAsReadSent.current,
                [lastUnreadMessage._id]: true,
            };

            webmessengerApi
                .markMessageAsRead(conversation._id, lastUnreadMessage._id)
                .catch(() => {
                    markMessagesAsReadSent.current = {
                        ...markMessagesAsReadSent.current,
                        [lastUnreadMessage._id]: false,
                    };
                });
        }
    }, [messages, conversation]);

    const { isLoading: messagesIsLoading } = useQuery(
        ["messages"],
        async () => await webmessengerApi.getMessages(conversation._id),
        {
            onSuccess: async (data) => {
                const messagesWithoutTyping = data.filter(
                    (m) =>
                        m.type !== "typing:visitor" &&
                        m.type !== "typing:operator"
                );

                handleSetMessages((prevMessages) => {
                    const messagesToPersist = prevMessages.filter(
                        (message) => message.status.error
                    );
                    return [
                        ...messagesToPersist,
                        ...messagesWithoutTyping,
                    ].sort(
                        (a, b) =>
                            new Date(a.createdAt || a.status.sentAt) -
                            new Date(b.createdAt || b.status.sentAt)
                    );
                });

                const typingMessage = data.find(
                    (m) => m.type === "typing:operator"
                );

                if (typingMessage) setTyping(true);

                /** Send received status */
                const unreceivedMessages = messagesWithoutTyping.filter(
                    (msg) => !msg.status.receivedAt
                );

                for (const msg of unreceivedMessages) {
                    if (msg.author?.type === "operator") {
                        await webmessengerApi
                            .markMessageAsReceived(conversation._id, msg._id)
                            .catch(() => {});
                    }
                }

                /** Send read status */
                markMessagesAsRead();
            },
            enabled: !!conversation?._id && connected,
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const groupMessages = (msgs) => {
        let groupKey = 0;

        return msgs
            .filter(
                (m) =>
                    m.type !== "typing" &&
                    m.content.type !== "assignment" &&
                    m.content.type !== "leadSent" &&
                    m.content.type !== "closed"
            )
            .reduce((res, message, key) => {
                if (key === 0) {
                    groupKey += 1;
                    return [
                        ...res,
                        {
                            ...(message.author
                                ? { author: message.author }
                                : {}),
                            ...(message.type === "system"
                                ? { type: "system" }
                                : {}),
                            messages: [message],
                        },
                    ];
                }
                if (key > 0 && key < messages.length) {
                    if (message.author?._id === res[groupKey - 1].author?._id) {
                        res[groupKey - 1].messages.push(message);
                    } else {
                        groupKey += 1;
                        res.push({
                            author: message.author,
                            messages: [message],
                            ...(message.type === "system"
                                ? { type: "system" }
                                : {}),
                        });
                    }
                    return res;
                }
                return res;
            }, []);
    };

    // if mobile & visualViewport change (keyboard open)
    useEffect(() => {
        let chatAutoScroll;

        if (windowSize.width < 769)
            chatAutoScroll = setTimeout(
                () =>
                    container.current.scrollTo({
                        top: container.current.scrollHeight,
                    }),
                100
            );

        return () => clearTimeout(chatAutoScroll);
    }, [visualViewport, windowSize]);

    useEffect(() => {
        if (conversation && messages && !document.hidden) {
            markMessagesAsRead();
        }
    }, [conversation, messages, document.hidden]);

    useEffect(() => {
        if (websocket && connected) {
            const onNewMessage = (message) => {
                if (message.conversation === conversation?._id) {
                    if (
                        message.author?.type === "visitor" &&
                        messages.find(({ _id }) => message._id === _id)
                    ) {
                        handleSetMessages((prevState) => {
                            const messagesCopy = [...prevState];

                            const messageIndex = messagesCopy.findIndex(
                                ({ _id }) => _id === message._id
                            );

                            if (messageIndex >= 0) {
                                messagesCopy[messageIndex] = {
                                    ...messagesCopy[messageIndex],
                                    ...message,
                                };
                            }

                            return messagesCopy;
                        });
                    } else {
                        handleSetMessages((prevState) => {
                            if (
                                !prevState.find(
                                    ({ _id }) => _id === message._id
                                )
                            )
                                return [
                                    ...prevState.filter(
                                        ({ _id }) => !_id.includes("behavior")
                                    ),
                                    message,
                                ].sort(
                                    (a, b) =>
                                        new Date(a.createdAt) -
                                        new Date(b.createdAt)
                                );

                            return prevState;
                        });
                    }

                    if (message.author?.type !== "visitor") setTyping(false);

                    if (
                        (message.author?.type === "operator" ||
                            message.author?.type === "agent") &&
                        conversation?._id
                    ) {
                        webmessengerApi
                            .markMessageAsReceived(
                                conversation._id,
                                message._id
                            )
                            .catch(() => {});
                    }
                }
            };
            websocket.on("message:new", onNewMessage);

            const onMessageUpdate = (res) => {
                handleSetMessages((previousState) => {
                    const newState = [...previousState];

                    for (const message of res.messages) {
                        const index = newState.findIndex(
                            (m) => m._id === message._id
                        );
                        newState[index] = message;
                    }

                    return newState;
                });
            };
            websocket.on("message:update", onMessageUpdate);

            const onConversationTyping = (res) => {
                setConversationTypings((prevState) => ({
                    ...prevState,
                    [res.typingId]: res.action,
                }));

                if (
                    res.action === "start" &&
                    conversationTypings[res.typingId] !== "stop"
                )
                    setTyping(true);
                if (res.action === "stop") setTyping(false);
            };
            websocket.on("conversation:typing", onConversationTyping);

            return () => {
                websocket.removeListener("message:new", onNewMessage);
                websocket.removeListener("message:update", onMessageUpdate);
                websocket.removeListener(
                    "conversation:typing",
                    onConversationTyping
                );
            };
        }

        return () => {};
    }, [
        websocket,
        connected,
        conversation,
        setTyping,
        conversationTypings,
        messages,
    ]);

    useAsyncEffect(async () => {
        const delay = (ms) =>
            new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        await delay(50);

        container.current.scrollTo({
            top: container.current.scrollHeight,
        });
    }, [messages, typing]);

    useEffect(() => {
        if (showTyping !== typing) {
            setShowTyping(typing);
        }
    }, [typing, showTyping]);

    useEffect(() => {
        if (conversation === null) {
            /**
             * Reset the messages only if there are messages in the state or there is no greeting message (from behaviors) found
             */
            if (
                messages.length > 0 &&
                !messages.find(({ _id }) => _id === "behavior-greeting-message")
            ) {
                handleSetMessages([]);
            }

            /**
             * Display the greeting message (from behaviors) only if it is not present in the state and it has not been sent
             */
            const getBehaviorsStore = getBehaviorsSessionStorage();

            const alreadyGreetingMessage = messages.find(
                ({ _id }) => _id === "behavior-greeting-message"
            );

            if (
                operator &&
                greeting?.message &&
                !alreadyGreetingMessage &&
                !getBehaviorsStore?.triggered?.greeting?.message?.sent
            ) {
                handleSetMessages([
                    {
                        _id: "behavior-greeting-message",
                        content: greeting.message.text,
                        type: "text",
                        zone: "EU",
                        status: {
                            sentAt: greeting.message.sentAt.toISO(),
                        },
                        author: {
                            ...operator,
                            type:
                                operator.type === "agent"
                                    ? "agent"
                                    : "operator",
                        },
                    },
                ]);
            }
        }
    }, [conversation, messages, greeting, operator]);

    return (
        <div className="ek-chat__conversation--wrapper" ref={container}>
            <div>
                {!messagesIsLoading &&
                    messages &&
                    groupMessages(messages).map((group) => (
                        <MessageGroup
                            key={`${
                                group.author?._id
                                    ? group.author._id
                                    : group.type
                            }-${group.messages[0]._id}`}
                            {...group}
                        />
                    ))}

                <Transition
                    as="div"
                    show={showTyping}
                    appear
                    unmount
                    className="ek-chat__message ek-chat__message--operator ek-chat__message--typing"
                    enterFrom="ek-chat__message--enterFrom"
                    enterTo="ek-chat__message--enterTo"
                    leave="ek-chat__message--leave"
                >
                    {operator?.face && (
                        <div className="ek-chat__avatar">
                            <img
                                src={
                                    operator?.type === "agent"
                                        ? `${process.env.REACT_APP_FILE}/files/agents-logo/${operator?.face}`
                                        : `${process.env.REACT_APP_FILE}/files/avatars/${operator?.face}`
                                }
                                alt=""
                                className="ek-chat__avatar--img"
                            />
                        </div>
                    )}

                    <div>
                        {/* animation genius typing */}
                        <p className="ek-chat__message--text">
                            <span />
                            <span />
                            <span />
                        </p>
                    </div>
                </Transition>

                {conversation && conversation.status === "closed" && (
                    <div className="ek-chat__message--error">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                            >
                                <g
                                    fill="none"
                                    fillRule="evenodd"
                                    stroke="none"
                                    strokeWidth="1"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        fill="currentColor"
                                        opacity=".3"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12.0355339,10.6213203 L14.863961,7.79289322 C15.2544853,7.40236893 15.8876503,7.40236893 16.2781746,7.79289322 C16.6686989,8.18341751 16.6686989,8.81658249 16.2781746,9.20710678 L13.4497475,12.0355339 L16.2781746,14.863961 C16.6686989,15.2544853 16.6686989,15.8876503 16.2781746,16.2781746 C15.8876503,16.6686989 15.2544853,16.6686989 14.863961,16.2781746 L12.0355339,13.4497475 L9.20710678,16.2781746 C8.81658249,16.6686989 8.18341751,16.6686989 7.79289322,16.2781746 C7.40236893,15.8876503 7.40236893,15.2544853 7.79289322,14.863961 L10.6213203,12.0355339 L7.79289322,9.20710678 C7.40236893,8.81658249 7.40236893,8.18341751 7.79289322,7.79289322 C8.18341751,7.40236893 8.81658249,7.40236893 9.20710678,7.79289322 L12.0355339,10.6213203 Z"
                                    />
                                </g>
                            </svg>
                            {isClosedByVisitor ? (
                                <p>
                                    <FormattedMessage
                                        id="WEBMESSENGER.MESSAGE_LIST.USER.LEFT_CONVERSATION"
                                        defaultMessage="Vous avez quitté la conversation."
                                    />
                                </p>
                            ) : (
                                <p>
                                    <FormattedMessage
                                        id="WEBMESSENGER.MESSAGE_LIST.LEFT_CONVERSATION"
                                        defaultMessage="Le conseiller a quitté la conversation."
                                    />
                                </p>
                            )}
                        </div>
                        {online && (
                            <button
                                type="button"
                                className="ek-chat--button"
                                onClick={() => {
                                    setConversation(null);
                                    removeCookie("eK-conversation", {
                                        path: "/",
                                    });
                                }}
                            >
                                <FormattedMessage
                                    id="WEBMESSENGER.MESSAGE_LIST.NEW_CONVERSATION"
                                    description="Texte : Démarrer une nouvelle conversation"
                                    defaultMessage="Démarrer une nouvelle conversation"
                                />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MessageList;
